import { useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { useEffect, useMemo } from "react"

import { TableViewConfig } from "@organisms/Table/Table.types"
import { migrateConfig } from "@organisms/Table/Table.utils"

import defaultTableViewConfig, { TableViewConfigType } from "@constants/defaultTableViewConfig"

const tableViewConfigAtom = atomWithStorage<TableViewConfigType>("objectsView", defaultTableViewConfig, undefined, {
    getOnInit: true,
})

export default function useTableViewConfiguration(
    tableStorageKey: string,
    defaultTableConfigOverride?: TableViewConfig,
) {
    const [currentTableViewConfig, setTableViewConfig] = useAtom(tableViewConfigAtom)

    const defaultTableConfig = defaultTableConfigOverride || defaultTableViewConfig.table[tableStorageKey]

    const persistedTableConfig = currentTableViewConfig.table[tableStorageKey]

    const effectiveTableConfig = useMemo(() => {
        if (!persistedTableConfig) {
            return defaultTableConfig
        }

        // A persisted entry can predate newly-shaped defaults (or have been written from a partial
        // update), so the defaults always back-fill any keys the stored config is missing.
        return { ...defaultTableConfig, ...persistedTableConfig }
    }, [persistedTableConfig, defaultTableConfig])

    useEffect(() => {
        if (!currentTableViewConfig || !defaultTableViewConfig) {
            return
        }

        const hasChanges = currentTableViewConfig.version !== defaultTableViewConfig.version

        if (hasChanges) {
            const migratedConfig = migrateConfig(currentTableViewConfig, defaultTableViewConfig)
            setTableViewConfig(migratedConfig)
        }
    }, [])

    const updateCurrentTableViewConfig = (
        key: keyof TableViewConfig,
        updatedValue: TableViewConfig[keyof TableViewConfig],
    ) => {
        setTableViewConfig((prevViews) => ({
            ...prevViews,
            table: {
                ...prevViews.table,
                [tableStorageKey]: {
                    // Tables configured through defaultTableConfigOverride have no entry in the
                    // global defaults, so seeding from the override keeps the first persisted
                    // write complete instead of storing only the updated key.
                    ...defaultTableConfig,
                    ...(prevViews.table[tableStorageKey] as TableViewConfig),
                    [key]: updatedValue,
                },
            },
        }))
    }

    // Read another table's entry as persisted, without this table's defaults or override.
    const getTableViewConfig = (storageKey: string) => currentTableViewConfig.table[storageKey]

    return {
        currentTableViewConfig: effectiveTableConfig,
        updateCurrentTableViewConfig,
        getTableViewConfig,
    }
}
